// Shot queries + mutations for the Shot Builder.
// Replaces the mock `mockDataStore` shot methods + provides shot detail.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../services/logger";
import { generateUUID } from "../../utils/uuid";
import { showErrorToast } from "@/utils/showErrorToast";
import {
  getShot,
  createShot,
  updateShot,
  deleteShot,
  reorderShots,
} from "@/services/api/sdk.gen";
import type { ShotDetail, ReorderShotUpdate } from "@/types/shots";
import { shotKeys } from "./shotKeys";
import { unwrapData } from "./unwrap";

/** Fetch a single shot's detail (workflow, counts, owner, nodes, …). */
export function useShotDetail(shotId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: shotKeys.shot(shotId ?? ""),
    queryFn: async () => {
      if (!shotId) throw new Error("Shot ID is required");
      const correlationId = generateUUID();
      logger.info("Fetching shot detail", { shotId, correlationId });
      const result = await getShot({ path: { shotId }, throwOnError: true });
      return unwrapData<ShotDetail | null>(result, null);
    },
    enabled: !!user && !!shotId,
    staleTime: 30 * 1000,
  });
}

interface CreateShotArgs {
  sceneId: string;
  name?: string;
  description?: string;
  shotSize?: string;
  cameraMovement?: string;
  aspectRatio?: string;
  durationMs?: number;
  workflowId?: string;
  templateWorkflowId?: string;
}

export function useCreateShot(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateShotArgs) => {
      const result = await createShot({
        path: { projectId },
        body: input,
        throwOnError: true,
      });
      return unwrapData<ShotDetail | null>(result, null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to create shot", error, projectId });
    },
  });
}

interface UpdateShotArgs {
  shotId: string;
  name?: string;
  description?: string;
  status?: string;
  shotSize?: string;
  cameraMovement?: string;
  aspectRatio?: string;
  durationMs?: number;
  sceneId?: string;
  order?: number;
  workflowId?: string;
  ownerId?: string;
}

export function useUpdateShot(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ shotId, ...body }: UpdateShotArgs) => {
      const result = await updateShot({
        path: { shotId },
        body,
        throwOnError: true,
      });
      return unwrapData<ShotDetail | null>(result, null);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
      queryClient.invalidateQueries({
        queryKey: shotKeys.shot(variables.shotId),
      });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to update shot", error, projectId });
    },
  });
}

export function useDeleteShot(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (shotId: string) => {
      await deleteShot({ path: { shotId }, throwOnError: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to delete shot", error, projectId });
    },
  });
}

export function useReorderShots(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: ReorderShotUpdate[]) => {
      await reorderShots({
        path: { projectId },
        body: { updates },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shotKeys.scenes(projectId) });
    },
    onError: (error) => {
      showErrorToast({ message: "Failed to reorder shots", error, projectId });
    },
  });
}
